import { useState } from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { stripePromise } from '@/features/wallet/services/stripeClient';
import { useWallet } from '@/app/providers/wallet';
import { useProcessing } from '@/app/providers/processing';
import { ErrorBanner } from '@/shared/ui/ErrorBanner';
import { Button, Spinner } from '@/shared/ui';
import { WalletActivityTable } from '@/features/wallet/components/WalletActivityTable';
import { WalletCardSection } from '@/features/wallet/components/WalletCardSection';
import { WalletAddFundsSection } from '@/features/wallet/components/WalletAddFundsSection';
import { balanceLevel } from '@/features/wallet/utils/walletBalance';
import { formatCredits } from '@/shared/utils/format';

function WalletPageInner() {
  const { balance, paymentMethod, loading, error, refreshBalance, refreshPaymentMethod, reload } = useWallet();
  const { jobs } = useProcessing();
  const [retrying, setRetrying] = useState(false);

  // Labels the held amount with what is actually holding it. Counted from the
  // in-flight jobs this session knows about, so it can lag the authoritative
  // held total (another device, or a job submitted before this tab loaded) —
  // hence the count is only ever a suffix on the amount, never the amount itself.
  const activeJobCount = jobs.filter(
    (job) => job.status === 'queued' || job.status === 'processing',
  ).length;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await reload();
    } finally {
      setRetrying(false);
    }
  };

  if (loading) {
    return (
      <div className="wallet__loading">
        <Spinner size={26} label="Loading your wallet" />
      </div>
    );
  }

  const level = balanceLevel(balance?.balanceCredits ?? null);

  return (
    <section className="wallet">
      <div className="wallet-head">
        <h1>Wallet</h1>
        <p>Buy credits ($1 = 1 credit) and spend them on video generation.</p>
      </div>

      {error && (
        <div className="wallet-load-error">
          <ErrorBanner message={error} />
          <Button onClick={handleRetry} loading={retrying}>
            {retrying ? 'Retrying…' : 'Retry'}
          </Button>
        </div>
      )}

      <div className="wallet-balance-card">
        <div className="wallet-balance-card__label">Available credits</div>
        <div className="wallet-balance-card__amount">
          {balance ? formatCredits(balance.balanceCredits) : '—'}
        </div>
        <div className="wallet-balance-card__updated">Credits available to spend on video generation</div>

        {/* Only shown while credits are actually reserved — with nothing in flight
            this would just be a permanent "0 on hold" row with nothing to explain. */}
        {balance && balance.onHoldCredits > 0 && (
          <div className="wallet-balance-card__hold">
            <div className="wallet-balance-card__hold-row">
              <span>
                On hold
                {activeJobCount > 0 && (
                  <> · {activeJobCount} generation{activeJobCount === 1 ? '' : 's'} in progress</>
                )}
              </span>
              <span className="wallet-balance-card__hold-amount">
                {formatCredits(balance.onHoldCredits)}
              </span>
            </div>
            <div className="wallet-balance-card__hold-row wallet-balance-card__hold-row--total">
              <span>Total credits</span>
              <span className="wallet-balance-card__hold-amount">
                {formatCredits(balance.totalCredits)}
              </span>
            </div>
            <p className="wallet-balance-card__hold-note">
              Credits reserved to cover generations that are still running. Each
              reservation includes a small buffer — whatever isn&rsquo;t used returns
              to your available credits when the generation finishes.
            </p>
          </div>
        )}
      </div>

      {level !== 'ok' && (
        <div className={`wallet-low-balance${level === 'empty' ? ' wallet-low-balance--empty' : ''}`}>
          {level === 'empty'
            ? 'You have no credits — add funds below to generate videos.'
            : 'Your credits are running low — top up below to keep generating.'}
        </div>
      )}

      <div className="wallet-grid">
        <WalletAddFundsSection
          balance={balance}
          paymentMethod={paymentMethod}
          refreshBalance={refreshBalance}
        />
        <WalletCardSection paymentMethod={paymentMethod} refreshPaymentMethod={refreshPaymentMethod} />
      </div>

      <WalletActivityTable balance={balance} />
    </section>
  );
}

export default function WalletPage() {
  // <Elements> must wrap everything that calls useStripe()/useElements(). Scoped to
  // this page rather than app-wide since only the wallet touches Stripe.
  return (
    <Elements stripe={stripePromise}>
      <WalletPageInner />
    </Elements>
  );
}

